'use client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { trackEvent } from '@/lib/analytics/gtm';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  CarLoanResult,
  ChattelMortgageResult,
  CommercialPropertyLoanResult,
  HirePurchaseResult,
  LoanRepaymentResult,
  useLoanRepaymentCalculator,
} from '@/lib/hooks/useLoanRepaymentCalculator';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentblocksloancalculators_Calculatortype,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  getCalculatorOutputAccordionStyles,
  OutputAlert,
  OutputField,
  OutputGraphic,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
  StyledToggleButtonField,
} from './components';

const T = Enum_Componentblocksloancalculators_Calculatortype;

const LOAN_TERM_YEARS = 10;
const MAX_LOAN_TERM_STANDARD = 30;
const MAX_VEHICLE_LOAN_TERM = 10;
const ROWS_PER_PAGE = 12;

type LoanCalculatorFormValues = {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
  balloonPaymentPercent: number;
  repaymentFrequency: 'monthly' | 'fortnightly' | 'weekly';
  vehiclePrice: number;
  deposit: number;
  gstClaimablePercent: number;
  propertyValue: number;
  stampDutyAndCosts: number;
};

const getDefaultValues = (
  loanType: Enum_Componentblocksloancalculators_Calculatortype
): LoanCalculatorFormValues => {
  switch (loanType) {
    case T.LoanRepayment:
    case T.Amortisation:
      return {
        loanAmount: 100000,
        interestRate: 7.5,
        loanTerm: 5,
        balloonPaymentPercent: 0,
        repaymentFrequency: 'monthly',
        vehiclePrice: 50000,
        deposit: 10000,
        gstClaimablePercent: 100,
        propertyValue: 700000,
        stampDutyAndCosts: 25000,
      };
    case T.CarLoan:
    case T.ChattelMortgage:
    case T.HirePurchase:
      return {
        loanAmount: 100000,
        interestRate: 7.5,
        loanTerm: 5,
        balloonPaymentPercent: 0,
        repaymentFrequency: 'monthly',
        vehiclePrice: 50000,
        deposit: 10000,
        gstClaimablePercent: 100,
        propertyValue: 700000,
        stampDutyAndCosts: 25000,
      };
    case T.CommercialPropertyLoan:
      return {
        loanAmount: 500000,
        interestRate: 6.5,
        loanTerm: 25,
        balloonPaymentPercent: 0,
        repaymentFrequency: 'monthly',
        vehiclePrice: 50000,
        deposit: 10000,
        gstClaimablePercent: 100,
        propertyValue: 700000,
        stampDutyAndCosts: 25000,
      };
    default:
      // BusinessLoan, LineOfCredit, EquipmentFinance — original defaults
      return {
        loanAmount: 100000,
        interestRate: 7.5,
        loanTerm: 3,
        balloonPaymentPercent: 30,
        repaymentFrequency: 'monthly',
        vehiclePrice: 50000,
        deposit: 10000,
        gstClaimablePercent: 100,
        propertyValue: 700000,
        stampDutyAndCosts: 25000,
      };
  }
};

type LoanCalculatorProps = {
  block?: ComponentBlocksLoanCalculators;
};

const LoanCalculator = ({ block }: LoanCalculatorProps) => {
  const theme = useTheme();
  const loanType = block?.CalculatorType || T.BusinessLoan;

  const {
    calculateBusinessLoanRepayments,
    calculateBusinessLineOfCreditRepayments,
    calculateEquipmentFinanceRepayments,
    calculateLoanRepayment,
    calculateCarLoan,
    calculateChattelMortgage,
    calculateHirePurchase,
    calculateCommercialPropertyLoan,
  } = useLoanRepaymentCalculator();

  const [page, setPage] = useState(1);
  const trackDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultValues = getDefaultValues(loanType);
  const methods = useForm<LoanCalculatorFormValues>({
    defaultValues,
  });
  const { control, watch } = methods;
  const values = watch();

  const trackCalcInteraction = (fieldName: string) => {
    if (trackDebounce.current) clearTimeout(trackDebounce.current);
    trackDebounce.current = setTimeout(() => {
      trackEvent({
        feature: 'calculator',
        action: 'calculator_interaction',
        params: { calculator_type: loanType, field_name: fieldName },
      });
    }, 600);
  };

  // ── Legacy calculations (BusinessLoan / LineOfCredit / EquipmentFinance — unchanged) ──
  const parsedInterestRate = values.interestRate || 0;
  const parsedLoanTermMonths = values.loanTerm * 12 || 0;
  const parsedBalloon = values.balloonPaymentPercent || 0;

  const legacyResult =
    loanType === T.LineOfCredit
      ? calculateBusinessLineOfCreditRepayments({
          loanAmount: values.loanAmount,
          interestRate: parsedInterestRate,
          loanTermMonths: parsedLoanTermMonths,
        })
      : loanType === T.EquipmentFinance
        ? calculateEquipmentFinanceRepayments({
            loanAmount: values.loanAmount,
            interestRate: parsedInterestRate,
            loanTermMonths: parsedLoanTermMonths,
            balloonPaymentPercent: parsedBalloon,
          })
        : calculateBusinessLoanRepayments({
            loanAmount: values.loanAmount,
            interestRate: parsedInterestRate,
            loanTermMonths: parsedLoanTermMonths,
          });

  // ── New type calculations ──────────────────────────────────────────────────
  const loanRepaymentResult: LoanRepaymentResult | null =
    loanType === T.LoanRepayment || loanType === T.Amortisation
      ? calculateLoanRepayment({
          loanAmount: values.loanAmount || 0,
          interestRate: values.interestRate || 0,
          loanTermYears: values.loanTerm || 0,
          balloonPaymentPercent: values.balloonPaymentPercent || 0,
          repaymentFrequency: values.repaymentFrequency || 'monthly',
        })
      : null;

  const carLoanResult: CarLoanResult | null =
    loanType === T.CarLoan
      ? calculateCarLoan({
          vehiclePrice: values.vehiclePrice || 0,
          deposit: values.deposit || 0,
          interestRate: values.interestRate || 0,
          loanTermYears: values.loanTerm || 0,
          balloonPaymentPercent: values.balloonPaymentPercent || 0,
        })
      : null;

  const chattelMortgageResult: ChattelMortgageResult | null =
    loanType === T.ChattelMortgage
      ? calculateChattelMortgage({
          vehiclePrice: values.vehiclePrice || 0,
          deposit: values.deposit || 0,
          interestRate: values.interestRate || 0,
          loanTermYears: values.loanTerm || 0,
          balloonPaymentPercent: values.balloonPaymentPercent || 0,
          gstClaimablePercent: values.gstClaimablePercent ?? 100,
        })
      : null;

  const hirePurchaseResult: HirePurchaseResult | null =
    loanType === T.HirePurchase
      ? calculateHirePurchase({
          vehiclePrice: values.vehiclePrice || 0,
          deposit: values.deposit || 0,
          interestRate: values.interestRate || 0,
          loanTermYears: values.loanTerm || 0,
          balloonPaymentPercent: values.balloonPaymentPercent || 0,
        })
      : null;

  const commercialPropertyResult: CommercialPropertyLoanResult | null =
    loanType === T.CommercialPropertyLoan
      ? calculateCommercialPropertyLoan({
          loanAmount: values.loanAmount || 0,
          propertyValue: values.propertyValue || 0,
          interestRate: values.interestRate || 0,
          loanTermYears: values.loanTerm || 0,
          stampDutyAndCosts: values.stampDutyAndCosts || 0,
        })
      : null;

  // ── Unified schedule for pagination ───────────────────────────────────────
  const activeSchedule =
    loanRepaymentResult?.schedule ??
    carLoanResult?.schedule ??
    chattelMortgageResult?.schedule ??
    hirePurchaseResult?.schedule ??
    commercialPropertyResult?.schedule ??
    legacyResult.monthly;

  const activeYearly =
    loanRepaymentResult?.yearly ??
    carLoanResult?.yearly ??
    chattelMortgageResult?.yearly ??
    hirePurchaseResult?.yearly ??
    commercialPropertyResult?.yearly ??
    legacyResult.yearly;

  const pageCount = Math.ceil(activeSchedule.length / ROWS_PER_PAGE);
  const paginatedSchedule = activeSchedule.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const scheduleAccordionLabel = loanRepaymentResult
    ? `${loanRepaymentResult.paymentLabel} Repayments`
    : 'Monthly Repayments';

  // ── Shared input fragments ─────────────────────────────────────────────────

  const interestRateInput = (
    <Controller
      name="interestRate"
      control={control}
      render={({ field }) => (
        <StyledTextSlider
          label="Interest Rate"
          value={field.value}
          defaultMax={30}
          step={0.01}
          suffix="%"
          onChange={(value) => {
            field.onChange(value);
            setPage(1);
            trackCalcInteraction('interest_rate');
          }}
        />
      )}
    />
  );

  const loanTermInput = (maxYears: number) => (
    <Controller
      name="loanTerm"
      control={control}
      render={({ field }) => (
        <StyledTextSlider
          label="Loan Term"
          value={field.value}
          minValue={1}
          maxValue={maxYears}
          suffix={`${field.value}` === '1' ? ' year' : ' years'}
          onChange={(value) => {
            field.onChange(value);
            setPage(1);
            trackCalcInteraction('loan_term');
          }}
        />
      )}
    />
  );

  const balloonInput = (
    <Controller
      name="balloonPaymentPercent"
      control={control}
      render={({ field }) => (
        <StyledTextSlider
          label="Balloon Payment"
          value={field.value}
          onChange={(numValue) => {
            const clamped = isNaN(numValue) || numValue < 0 ? 0 : numValue > 100 ? 100 : numValue;
            field.onChange(clamped);
            setPage(1);
            trackCalcInteraction('balloon_payment');
          }}
          step={0.1}
          minValue={0}
          maxValue={100}
          defaultMax={100}
          suffix="%"
          helperText="*Percentage of loan amount paid at end"
        />
      )}
    />
  );

  const vehiclePriceInput = (
    <Controller
      name="vehiclePrice"
      control={control}
      render={({ field }) => (
        <StyledTextSlider
          label="Vehicle Price"
          value={field.value}
          prefix="$"
          onChange={(value) => {
            field.onChange(value);
            setPage(1);
            trackCalcInteraction('vehicle_price');
          }}
        />
      )}
    />
  );

  const depositInput = (
    <Controller
      name="deposit"
      control={control}
      render={({ field }) => (
        <StyledTextSlider
          label="Deposit"
          value={field.value}
          prefix="$"
          onChange={(value) => {
            field.onChange(value);
            setPage(1);
            trackCalcInteraction('deposit');
          }}
        />
      )}
    />
  );

  // ── Schedule table (shared across types) ──────────────────────────────────

  const scheduleTable = (
    <>
      <Table stickyHeader size="small" sx={{ width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Payment ($)</TableCell>
            <TableCell>Interest ($)</TableCell>
            <TableCell>Principal ($)</TableCell>
            <TableCell>Balance ($)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedSchedule.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>{row.Number}</TableCell>
              <TableCell>{row.Payment}</TableCell>
              <TableCell>{row.Interest}</TableCell>
              <TableCell>{row.Principal}</TableCell>
              <TableCell>{row.Balance}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </>
  );

  const yearlyTable = activeYearly.length > 0 && (
    <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
        <Typography>Yearly Overview</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Table stickyHeader size="small" sx={{ width: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell>Total Payments ($)</TableCell>
              <TableCell>Total Interest ($)</TableCell>
              <TableCell>Total Principal ($)</TableCell>
              <TableCell>Remaining Balance ($)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeYearly.map((year, idx) => (
              <TableRow key={idx}>
                <TableCell>{year.Year}</TableCell>
                <TableCell>{year.totalpayments}</TableCell>
                <TableCell>{year.totalinterest}</TableCell>
                <TableCell>{year.totalprincipal}</TableCell>
                <TableCell>{year.totalbalance}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );

  const scheduleAccordion = (
    <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
        <Typography>{scheduleAccordionLabel}</Typography>
      </AccordionSummary>
      <AccordionDetails>{scheduleTable}</AccordionDetails>
    </Accordion>
  );

  // ── Input rendering by type ────────────────────────────────────────────────

  const renderInputs = () => {
    // LoanRepayment / Amortisation
    if (loanType === T.LoanRepayment || loanType === T.Amortisation) {
      return (
        <>
          <Controller
            name="repaymentFrequency"
            control={control}
            render={({ field }) => (
              <StyledToggleButtonField
                label="Repayment Frequency"
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  setPage(1);
                  trackCalcInteraction('repayment_frequency');
                }}
                sx={{
                  mb: 3,
                  '& .MuiToggleButtonGroup-root': {
                    display: { xs: 'flex', md: 'row' },
                    flexDirection: 'row',
                  },
                }}
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'fortnightly', label: 'Fortnightly' },
                  { value: 'weekly', label: 'Weekly' },
                ]}
              />
            )}
          />
          <Controller
            name="loanAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Amount"
                value={field.value}
                defaultMax={defaultValues.loanAmount > 500000 ? defaultValues.loanAmount : 500000}
                prefix="$"
                onChange={(value) => {
                  field.onChange(value);
                  setPage(1);
                  trackCalcInteraction('loan_amount');
                }}
              />
            )}
          />
          {interestRateInput}
          {loanTermInput(MAX_LOAN_TERM_STANDARD)}
          {balloonInput}
        </>
      );
    }

    // CarLoan
    if (loanType === T.CarLoan) {
      return (
        <>
          {vehiclePriceInput}
          {depositInput}
          {interestRateInput}
          {loanTermInput(MAX_VEHICLE_LOAN_TERM)}
          {balloonInput}
        </>
      );
    }

    // ChattelMortgage
    if (loanType === T.ChattelMortgage) {
      return (
        <>
          {vehiclePriceInput}
          {depositInput}
          {interestRateInput}
          {loanTermInput(MAX_VEHICLE_LOAN_TERM)}
          {balloonInput}
          <Controller
            name="gstClaimablePercent"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="GST Claimable"
                value={field.value}
                minValue={0}
                maxValue={100}
                defaultMax={100}
                step={1}
                suffix="%"
                helperText="*Percentage of GST you can claim back"
                onChange={(value) => {
                  field.onChange(value);
                  trackCalcInteraction('gst_claimable_percent');
                }}
              />
            )}
          />
        </>
      );
    }

    // HirePurchase
    if (loanType === T.HirePurchase) {
      return (
        <>
          {vehiclePriceInput}
          {depositInput}
          {interestRateInput}
          {loanTermInput(MAX_VEHICLE_LOAN_TERM)}
          {balloonInput}
        </>
      );
    }

    // CommercialPropertyLoan
    if (loanType === T.CommercialPropertyLoan) {
      return (
        <>
          <Controller
            name="loanAmount"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Loan Amount"
                value={field.value}
                prefix="$"
                defaultMax={defaultValues.loanAmount > 500000 ? defaultValues.loanAmount : 500000}
                onChange={(value) => {
                  field.onChange(value);
                  setPage(1);
                  trackCalcInteraction('loan_amount');
                }}
              />
            )}
          />
          <Controller
            name="propertyValue"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Property Value"
                value={field.value}
                prefix="$"
                defaultMax={
                  defaultValues.propertyValue > 500000 ? defaultValues.propertyValue : 500000
                }
                onChange={(value) => {
                  field.onChange(value);
                  trackCalcInteraction('property_value');
                }}
              />
            )}
          />
          {interestRateInput}
          {loanTermInput(MAX_LOAN_TERM_STANDARD)}
          <Controller
            name="stampDutyAndCosts"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Stamp Duty & Costs"
                value={field.value}
                prefix="$"
                defaultMax={200000}
                onChange={(value) => {
                  field.onChange(value);
                  trackCalcInteraction('stamp_duty_and_costs');
                }}
              />
            )}
          />
        </>
      );
    }

    // Legacy types: BusinessLoan, LineOfCredit, EquipmentFinance (unchanged)
    return (
      <>
        <Controller
          name="loanAmount"
          control={control}
          render={({ field }) => (
            <StyledTextSlider
              label="Loan Amount"
              value={field.value}
              prefix="$"
              defaultMax={defaultValues.loanAmount > 500000 ? defaultValues.loanAmount : 500000}
              onChange={(value) => {
                field.onChange(value);
                setPage(1);
                trackCalcInteraction('loan_amount');
              }}
            />
          )}
        />

        <Controller
          name="interestRate"
          control={control}
          render={({ field }) => (
            <StyledTextSlider
              label="Interest Rate"
              value={field.value}
              defaultMax={30}
              step={0.01}
              suffix="%"
              onChange={(value) => {
                field.onChange(value);
                setPage(1);
                trackCalcInteraction('interest_rate');
              }}
            />
          )}
        />

        <Controller
          name="loanTerm"
          control={control}
          render={({ field }) => (
            <StyledTextSlider
              label="Loan Term"
              value={field.value}
              minValue={1}
              maxValue={LOAN_TERM_YEARS}
              suffix={`${field.value}` === '1' ? ' year' : ' years'}
              onChange={(value) => {
                field.onChange(value);
                setPage(1);
                trackCalcInteraction('loan_term');
              }}
            />
          )}
        />

        {loanType === T.EquipmentFinance && (
          <Controller
            name="balloonPaymentPercent"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Balloon Payment"
                value={field.value}
                onChange={(numValue) => {
                  const clamped =
                    isNaN(numValue) || numValue < 0 ? 0 : numValue > 100 ? 100 : numValue;
                  field.onChange(clamped);
                  setPage(1);
                  trackCalcInteraction('balloon_payment');
                }}
                step={0.1}
                minValue={0}
                maxValue={100}
                defaultMax={100}
                suffix="%"
                helperText="*Percentage of loan amount paid at end"
              />
            )}
          />
        )}
      </>
    );
  };

  // ── Output rendering by type ───────────────────────────────────────────────

  const renderOutputs = () => {
    // LoanRepayment
    if (loanType === T.LoanRepayment && loanRepaymentResult) {
      return (
        <>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Principal',
                  value: loanRepaymentResult.loanAmount,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(loanRepaymentResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Total Interest" value={`$${loanRepaymentResult.totalInterest}`} />
              <OutputField label="Total Repaid" value={`$${loanRepaymentResult.totalRepaid}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <OutputField
            label={`${loanRepaymentResult.paymentLabel} Payment`}
            value={`$${loanRepaymentResult.periodicPayment}`}
          />
          <StyledColumn>
            {scheduleAccordion}
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // Amortisation — same calculations, schedule shown prominently
    if (loanType === T.Amortisation && loanRepaymentResult) {
      return (
        <>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Principal',
                  value: loanRepaymentResult.loanAmount,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(loanRepaymentResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Total Interest" value={`$${loanRepaymentResult.totalInterest}`} />
              <OutputField label="Total Repaid" value={`$${loanRepaymentResult.totalRepaid}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <OutputField
            label={`${loanRepaymentResult.paymentLabel} Payment`}
            value={`$${loanRepaymentResult.periodicPayment}`}
          />
          <StyledColumn>
            <Accordion defaultExpanded sx={getCalculatorOutputAccordionStyles(theme)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
                <Typography>Amortisation Schedule</Typography>
              </AccordionSummary>
              <AccordionDetails>{scheduleTable}</AccordionDetails>
            </Accordion>
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // CarLoan
    if (loanType === T.CarLoan && carLoanResult) {
      return (
        <>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Net Loan',
                  value: carLoanResult.netLoan,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(carLoanResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField
                label="Net Loan Amount"
                value={`$${carLoanResult.netLoan.toLocaleString('en-AU', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
              <OutputField label="Total Interest" value={`$${carLoanResult.totalInterest}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label="Monthly Payment" value={`$${carLoanResult.periodicPayment}`} />
            <OutputField label="Total Repaid" value={`$${carLoanResult.totalRepaid}`} />
          </Styled2ColGrid>
          <OutputField
            label="Avg. Annual Interest Cost"
            value={`$${carLoanResult.avgAnnualInterest}`}
          />
          <StyledColumn>
            {scheduleAccordion}
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // ChattelMortgage
    if (loanType === T.ChattelMortgage && chattelMortgageResult) {
      return (
        <>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Net Loan',
                  value: chattelMortgageResult.netLoan,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(chattelMortgageResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
                {
                  label: 'GST',
                  value: parseFloat(chattelMortgageResult.gstClaimable.replace(/,/g, '')),
                  color: theme.palette.brand.primarySecondary1,
                },
              ]}
            />
            <StyledColumn>
              <OutputField
                label="Total Interest"
                value={`$${chattelMortgageResult.totalInterest}`}
              />
              <OutputField label="GST Claimable" value={`$${chattelMortgageResult.gstClaimable}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField
              label="Monthly Payment"
              value={`$${chattelMortgageResult.periodicPayment}`}
            />
            <OutputField label="Total Repaid" value={`$${chattelMortgageResult.totalRepaid}`} />
          </Styled2ColGrid>
          <OutputField
            label="Net Cost After GST Claim"
            value={`$${chattelMortgageResult.netCostAfterGST}`}
          />
          <StyledColumn>
            {scheduleAccordion}
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // HirePurchase
    if (loanType === T.HirePurchase && hirePurchaseResult) {
      return (
        <>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Net Loan',
                  value: hirePurchaseResult.netLoan,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(hirePurchaseResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Total Interest" value={`$${hirePurchaseResult.totalInterest}`} />
              <OutputField label="Total Repaid" value={`$${hirePurchaseResult.totalRepaid}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label="Monthly Payment" value={`$${hirePurchaseResult.periodicPayment}`} />
            <OutputField
              label="Total Acquisition Cost"
              value={`$${hirePurchaseResult.totalAcquisitionCost}`}
            />
          </Styled2ColGrid>
          <OutputField
            label="Avg. Annual Interest Cost"
            value={`$${hirePurchaseResult.avgAnnualInterest}`}
          />
          <StyledColumn>
            {scheduleAccordion}
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // CommercialPropertyLoan
    if (loanType === T.CommercialPropertyLoan && commercialPropertyResult) {
      return (
        <>
          {commercialPropertyResult.lvrWarning && (
            <OutputAlert>
              LVR of {commercialPropertyResult.lvr.toFixed(1)}% exceeds the typical 70% maximum for
              commercial property loans.
            </OutputAlert>
          )}
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Principal',
                  value: values.loanAmount || 0,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Interest',
                  value: parseFloat(commercialPropertyResult.totalInterest.replace(/,/g, '')),
                  color: theme.palette.brand.secondary,
                },
                {
                  label: 'Stamp Duty & Costs',
                  value: values.stampDutyAndCosts || 0,
                  color: theme.palette.brand.primarySecondary1,
                },
              ]}
            />
            <StyledColumn>
              <OutputField
                label="Monthly Payment"
                value={`$${commercialPropertyResult.monthlyPayment}`}
              />
              <OutputField
                label="Total Interest"
                value={`$${commercialPropertyResult.totalInterest}`}
              />
              <OutputField
                label="Total Repaid"
                value={`$${commercialPropertyResult.totalRepaid}`}
              />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField
              label={`LVR${commercialPropertyResult.lvrWarning ? ' ⚠' : ''}`}
              value={`${commercialPropertyResult.lvr.toFixed(1)}%`}
            />
            <OutputField
              label="Total Cost incl. Stamp Duty & Costs"
              value={`$${commercialPropertyResult.totalCostIncludingDuty}`}
            />
          </Styled2ColGrid>
          <StyledColumn>
            {scheduleAccordion}
            {yearlyTable}
          </StyledColumn>
        </>
      );
    }

    // Legacy types: BusinessLoan, LineOfCredit, EquipmentFinance (unchanged)
    const monthlySchedule = legacyResult?.monthly || [];
    const legacyPageCount = Math.ceil(monthlySchedule.length / ROWS_PER_PAGE);
    const legacyPaginatedSchedule = monthlySchedule.slice(
      (page - 1) * ROWS_PER_PAGE,
      page * ROWS_PER_PAGE
    );

    return (
      <>
        <Styled2ColGrid>
          <OutputGraphic
            data={[
              {
                label: 'Principal',
                value: legacyResult.loanAmount,
                color: theme.palette.brand.primary,
              },
              {
                label: 'Interest',
                value: parseFloat(legacyResult.totalInterest.replace(/[^0-9.]/g, '')),
                color: theme.palette.brand.secondary,
              },
            ]}
          />
          <StyledColumn>
            <OutputField label="Total Interest" value={`$${legacyResult.totalInterest}`} />
            <OutputField label="Total Payment" value={`$${legacyResult.totalPayment}`} />
          </StyledColumn>
        </Styled2ColGrid>
        <OutputField label="Monthly Payment" value={`$${legacyResult.monthlyPayment}`} />
        <StyledColumn>
          <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
              <Typography>Monthly Repayments</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table stickyHeader size="small" sx={{ width: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Payment ($)</TableCell>
                    <TableCell>Interest ($)</TableCell>
                    <TableCell>Principal ($)</TableCell>
                    <TableCell>Balance ($)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {legacyPaginatedSchedule.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.Number}</TableCell>
                      <TableCell>{row.Payment}</TableCell>
                      <TableCell>{row.Interest}</TableCell>
                      <TableCell>{row.Principal}</TableCell>
                      <TableCell>{row.Balance}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {legacyPageCount > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  <Pagination
                    count={legacyPageCount}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          {legacyResult?.yearly?.length > 0 && (
            <Accordion sx={getCalculatorOutputAccordionStyles(theme)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 30 }} />}>
                <Typography>Yearly Overview</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table stickyHeader size="small" sx={{ width: 'auto' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Year</TableCell>
                      <TableCell>Total Payments ($)</TableCell>
                      <TableCell>Total Interest ($)</TableCell>
                      <TableCell>Total Principal ($)</TableCell>
                      <TableCell>Remaining Balance ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {legacyResult.yearly.map((year, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{year.Year}</TableCell>
                        <TableCell>{year.totalpayments}</TableCell>
                        <TableCell>{year.totalinterest}</TableCell>
                        <TableCell>{year.totalprincipal}</TableCell>
                        <TableCell>{year.totalbalance}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          )}
        </StyledColumn>
      </>
    );
  };

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'xs',
          } as StrapiDisplayOptions
        }
        dataComponent="LoanCalculator"
        rowLayout={true}
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Select loan terms'}>
          {renderInputs()}
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Loan Summary'}>
          {renderOutputs()}
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default LoanCalculator;
